import { Component, computed, effect, inject, input, OnChanges, OnInit, signal, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { comIdempotencia, novaChaveIdempotencia } from 'src/interceptors/idempotencia-interceptor';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Pessoa } from '@/shared/models/pessoa-info.model';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ConfirmDialog, ConfirmDialogModule } from 'primeng/confirmdialog';
import { finalize } from 'rxjs/operators';
import { AnotacoesFeatureService } from './anotacoes.service';
import { EInconPos } from '@/shared/components/layout/botao-acoes/botao-acoes';
import { TabContainerOptionType } from '../layout/tab-container/tab-container';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TooltipModule } from 'primeng/tooltip';
import { ButtonModule } from 'primeng/button';
import { BadgeModule } from 'primeng/badge';
import { DialogModule } from 'primeng/dialog';
import { NovaAnotacaoDialog } from '@/shared/components/anotacoes/dialogs/nova-anotacao/nova-anotacao';
import { CustomTableComponent } from '../layout/custom-table/custom-table.component';
import { CardContainer } from '@/shared/components/card-container/card-container';
import { CardContent } from '@/shared/components/card-content/card-content';
import { Page } from '@/shared/models/page.model';
import { firstValueFrom } from 'rxjs';
import { PermissaoService } from '@/core/api/sicom-api/portal/permissao/permissao.service';
import { ContextoRotaRegistroService } from '@/shared/services/contexto-rota-registro.service';
import { CanDirective } from '@/core/permissions/can.directive';
import { PermissionService } from '@/core/permissions/permission.service';
import { PermissionInput } from '@/core/permissions/permission.model';
import { InputTextModule } from 'primeng/inputtext';
import { MedicoAnotacaoInsertRequest } from '@/core/api/sicom-api/registro/medico/anotacao/anotacao.model';
import { FiltroCampo } from '@/shared/components/layout/filtros-bar/filtro-campo';
import { FiltrosBar } from '@/shared/components/layout/filtros-bar/filtros-bar';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
interface AnotacaoRow {
    codigo: number;
    data: string;
    historico: string;
}

@Component({
    selector: 'app-anotacoes',
    standalone: true,
    imports: [
        DialogoPadraoDirective,
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        ConfirmDialogModule,
        ConfirmDialog,
        SelectButtonModule,
        TooltipModule,
        ButtonModule,
        BadgeModule,
        DialogModule,
        NovaAnotacaoDialog,
        CustomTableComponent,
        CardContainer,
        CardContent,
        CanDirective,
        InputTextModule,
        FiltrosBar,
        FiltroCampo
    ],
    providers: [ConfirmationService],
    templateUrl: './anotacoes.html',
    styleUrl: './anotacoes.scss'
})
export class Anotacoes implements OnInit, OnChanges {
    protected readonly EInconPos = EInconPos;

    pessoa = input<Pessoa>();
    readonly roleCriar = input<PermissionInput | undefined>(undefined);
    readonly roleEditar = input<PermissionInput | undefined>(undefined);
    readonly roleExcluir = input<PermissionInput | undefined>(undefined);
    private readonly permCan = inject(PermissionService);

    /** True se a role foi passada E o usuário não a tem (sem role → sempre permitido). */
    private semPermissao(role: PermissionInput | undefined): boolean {
        return !!role && !this.permCan.can(role);
    }

    protected readonly viewOptions = [
        { label: 'Cards', value: 'cards', icon: 'pi pi-th-large' },
        { label: 'Tabela', value: 'table', icon: 'pi pi-list' }
    ];

    options: TabContainerOptionType[] = [
        {
            label: 'Geral',
            value: 'gerais',
            actions: {
                items: [
                    { label: 'Gerar', icon: 'pi pi-plus-circle', disabled: false, command: () => this.abrirDialogNovaAnotacao(), styleClass: 'bg-indigo-500' },
                    { label: 'Remover', icon: 'pi pi-trash', disabled: true, command: () => this.remover(this.selectedHistoricoMedico()!.codigo), styleClass: 'bg-red-500' }
                ]
            }
        },
        { label: 'Inscrição', value: 'inscricao' },
        { label: 'Portal', value: 'portal' },
        { label: 'RGM', value: 'rgm' },
        { label: 'Provisório', value: 'provisorio' },
        { label: 'Documentos', value: 'documentos' },
        { label: 'Eleições', value: 'eleicoes' }
    ];

    selectedValue: string | null = null;
    selectedHistoricoMedico = signal<AnotacaoRow | null>(null);
    anotacaoVisualizacao = signal<AnotacaoRow | null>(null);
    modalVisualizacaoAnotacao = false;

    linhas = signal<AnotacaoRow[]>([]);
    /** Texto digitado na barra de filtros — rascunho, só vale depois do "Filtrar". */
    filtroTexto = signal('');
    /** Texto efetivamente aplicado à lista, atualizado apenas por {@link aplicarFiltros}. */
    private readonly filtroTextoAplicado = signal('');
    sortDescendente = signal(true);
    /** Direção enviada ao backend (ordenação server-side por data, em todas as páginas). */
    protected readonly direcao = computed<'ASC' | 'DESC'>(() => (this.sortDescendente() ? 'DESC' : 'ASC'));

    /** Estado que a tabela usa para marcar a seta do rótulo e o badge ativo. Só a data ordena. */
    protected readonly sortState = computed<{ field: string; order: 1 | -1 }>(() => ({ field: 'data', order: this.sortDescendente() ? -1 : 1 }));

    // A ordenação é server-side (ver direcao/atualizarTabela); aqui só o filtro de texto da página atual.
    linhasFiltradas = computed(() => {
        const q = this.filtroTextoAplicado().trim().toLowerCase();
        const items = this.linhas();
        return q ? items.filter((i) => i.historico?.toLowerCase().includes(q)) : items;
    });

    loading = signal(false);
    erro = signal<string | undefined>(undefined);

    totalRecords = signal(0);
    first = signal(0);
    rows = signal(10);

    totalizadores = computed(() => ({ registros: this.totalRecords() }));
    private permissaoEscritaAnotacoesGerais = signal(true);
    private cicloPermissaoEscritaAnotacoesGerais = 0;

    initialViewMode = computed<'table' | 'card'>(() => (this.contextoRotaRegistroService.ehSistemaRegistro() ? 'table' : 'card'));

    tableMetadata() {
        const isGerais = this.selectedValue === 'gerais';
        const headers: any = {
            data: { type: 'text', value: 'Data', hidden: false, default: '', orderBy: { asc: true } },
            historico: { type: 'text', value: 'Histórico', hidden: false, default: '' }
        };

        const actions: any = {};
        // Paleta do grid de pagamentos: a ação de sempre fica na cor primária do tema e só a
        // destrutiva puxa o vermelho. Vale para o card e para a coluna de ações da tabela.
        actions['visualizar'] = {
            label: 'Visualizar',
            icon: 'pi pi-eye',
            color: 'primary',
            command: (item: AnotacaoRow) => this.visualizarAnotacao(item)
        };

        if (isGerais && this.podeExibirAcoesGerais() && !this.semPermissao(this.roleExcluir())) {
            actions['remover'] = {
                label: 'Remover',
                icon: 'pi pi-trash',
                color: 'danger',
                command: (item: AnotacaoRow) => this.remover(item.codigo)
            };
        }

        return {
            headers,
            title: 'historico',
            tag: 'data',
            badgeFields: ['data'],
            actions: Object.keys(actions).length > 0 ? actions : undefined
        };
    }

    obterHistoricoExibicao(anotacao: Partial<AnotacaoRow> | null | undefined): string {
        return (anotacao?.historico || '').trim() || 'N/A';
    }

    modalNovaAnotacao = false;

    /**
     * Chave de idempotência da sessão do diálogo: nasce ao abrir e vale para todas as tentativas
     * dali. Reabrir gera outra, que é como o usuário registra duas anotações iguais de propósito.
     */
    private chaveIdempotencia = novaChaveIdempotencia();
    selectedTab = 'gerais';

    private readonly permissaoService = inject(PermissaoService);

    constructor(
        private messageService: MessageService,
        private confirmationService: ConfirmationService,
        private anotacoesService: AnotacoesFeatureService,
        private contextoRotaRegistroService: ContextoRotaRegistroService
    ) {
        effect(() => {
            const hasSelection = this.selectedHistoricoMedico() !== null;
            const podeGerenciarGerais = this.podeGerenciarAnotacoesGerais();
            const itemsUpdated = [
                { label: 'Gerar', icon: 'pi pi-plus-circle', disabled: !podeGerenciarGerais || this.semPermissao(this.roleCriar()), command: () => this.abrirDialogNovaAnotacao(), styleClass: 'bg-indigo-500' },
                { label: 'Remover', icon: 'pi pi-trash', disabled: !podeGerenciarGerais || !hasSelection || this.semPermissao(this.roleExcluir()), command: () => this.remover(this.selectedHistoricoMedico()!.codigo), styleClass: 'bg-red-500' }
            ];

            const geralIndex = this.options.findIndex((opt) => opt.value === 'gerais');
            if (geralIndex !== -1 && this.options[geralIndex].actions) {
                this.options[geralIndex].actions.items = itemsUpdated;
            }
        });
    }

    ngOnInit(): void {
        this.selectedValue = this.options[0].value;
        void this.sincronizarPermissaoEAtualizarTabela();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['pessoa']) {
            this.resetPaginacao();
            void this.sincronizarPermissaoEAtualizarTabela();
        }
    }

    onChange(selectedTab: string): void {
        this.filtroTexto.set('');
        this.filtroTextoAplicado.set('');
        this.selectedTab = selectedTab;
        this.selectedValue = selectedTab;
        this.selectedHistoricoMedico.set(null);
        this.resetPaginacao();
        void this.sincronizarPermissaoEAtualizarTabela();
    }

    /**
     * Aplica o texto digitado à lista. Como no `grid-pagamento`, o filtro fica pendente enquanto
     * se digita: quem está montando a busca não quer a lista mudando a cada tecla.
     */
    aplicarFiltros(): void {
        this.filtroTextoAplicado.set(this.filtroTexto());
    }

    limparFiltros(): void {
        this.filtroTexto.set('');
        this.filtroTextoAplicado.set('');
    }

    onPageChange(event: { first?: number; rows?: number }): void {
        const novoFirst = event.first ?? 0;
        const novasRows = event.rows ?? this.rows();
        this.first.set(novoFirst);
        this.rows.set(novasRows);
        this.atualizarTabela();
    }

    /**
     * Ponte do `sortData` da tabela: o rótulo "Data" na tabela e o badge de ordenação no modo card
     * caem aqui. A listagem só ordena por data — o backend recebe apenas a direção —, então
     * qualquer outra coluna é ignorada. Ordem `0` (terceiro clique) também: a lista sempre sai
     * ordenada.
     */
    onSortData(evento: { field?: string; order?: -1 | 0 | 1 }): void {
        if (evento.field !== 'data' || !evento.order) return;
        this.onAlternarOrdenacao(evento.order === -1);
    }

    /** Alterna a ordenação por data (server-side): volta à primeira página e recarrega todas as páginas ordenadas. */
    onAlternarOrdenacao(descendente: boolean): void {
        this.sortDescendente.set(descendente);
        this.resetPaginacao();
        this.atualizarTabela();
    }

    protected podeExibirAcoesGerais(): boolean {
        return this.selectedValue === 'gerais' && this.permissaoEscritaAnotacoesGerais();
    }

    private podeGerenciarAnotacoesGerais(): boolean {
        return this.selectedValue === 'gerais' && this.permissaoEscritaAnotacoesGerais();
    }

    private async sincronizarPermissaoEAtualizarTabela(): Promise<void> {
        await this.atualizarPermissaoEscritaAnotacoesGerais();
        this.atualizarTabela();
    }

    private async atualizarPermissaoEscritaAnotacoesGerais(): Promise<void> {
        const cicloAtual = ++this.cicloPermissaoEscritaAnotacoesGerais;

        if (!this.contextoRotaRegistroService.ehRotaAnotacoesMedico()) {
            this.permissaoEscritaAnotacoesGerais.set(true);
            return;
        }

        if (this.selectedValue !== 'gerais') {
            this.permissaoEscritaAnotacoesGerais.set(true);
            return;
        }

        const ufPessoaPesquisada = (this.pessoa()?.uf || '').trim().toUpperCase();
        if (!ufPessoaPesquisada) {
            this.permissaoEscritaAnotacoesGerais.set(false);
            return;
        }

        this.permissaoEscritaAnotacoesGerais.set(false);
        try {
            const resultado = await firstValueFrom(this.permissaoService.validar({ validacoes: { mesmaUf: ufPessoaPesquisada } }));

            if (cicloAtual !== this.cicloPermissaoEscritaAnotacoesGerais) {
                return;
            }

            this.permissaoEscritaAnotacoesGerais.set(!!resultado.validacoes['mesmaUf']);
        } catch {
            if (cicloAtual !== this.cicloPermissaoEscritaAnotacoesGerais) {
                return;
            }

            this.permissaoEscritaAnotacoesGerais.set(false);
        }
    }

    private resetPaginacao(): void {
        this.first.set(0);
    }

    private atualizarTabela(): void {
        const key = this.selectedTab ?? 'gerais';
        const p = this.pessoa();
        if (!p || p.id === undefined || p.id === null || !p.uf) {
            this.linhas.set([]);
            this.totalRecords.set(0);
            return;
        }

        this.carregarAba(key, p);
    }

    private carregarAba(key: string, p: Pessoa): void {
        const params: Pessoa = { id: p.id, uf: p.uf };
        const config = this.getAbaConfig(key);

        if (!config) {
            this.linhas.set([]);
            this.totalRecords.set(0);
            return;
        }

        const size = this.rows();
        const page = Math.floor(this.first() / size);

        this.loading.set(true);
        this.erro.set(undefined);

        config
            .service(params, page, size, this.direcao())
            .pipe(finalize(() => this.loading.set(false)))
            .subscribe({
                next: (resp: Page<any>) => {
                    const content = resp?.content ?? [];
                    this.linhas.set(content.map((item: any, idx: number) => config.mapper(item, idx + page * size)));
                    this.totalRecords.set(resp?.totalElements ?? content.length);
                },
                error: (err: any) => {
                    console.error(config.errorMsg, err);
                    this.linhas.set([]);
                    this.totalRecords.set(0);
                    this.erro.set(config.errorMsg);
                }
            });
    }

    private getAbaConfig(key: string) {
        const configs: Record<string, { service: (p: Pessoa, page: number, size: number, direction: 'ASC' | 'DESC') => any; mapper: (a: any, idx?: number) => AnotacaoRow; errorMsg: string }> = {
            gerais: {
                service: (p, page, size, direction) => this.anotacoesService.buscar(p, page, size, direction),
                mapper: (a) => ({ codigo: a.idHistoricoMedico ?? 0, data: this.formatarData(a.dtHistoricoMedico), historico: a.txHistoricoMedico ?? '' }),
                errorMsg: 'Falha ao carregar anotações gerais.'
            },
            inscricao: {
                service: (p, page, size, direction) => this.anotacoesService.buscarInscricao(p, page, size, direction),
                mapper: (a, idx) => ({ codigo: (idx ?? 0) + 1, data: this.formatarData(a.dtHistoricoMedico), historico: a.txHistoricoMedico ?? '' }),
                errorMsg: 'Falha ao carregar anotações de inscrição.'
            },
            portal: {
                service: (p, page, size, direction) => this.anotacoesService.buscarPortal(p, page, size, direction),
                mapper: (a) => ({ codigo: a.idHistoricoMedico ?? 0, data: this.formatarData(a.dtHistoricoMedico), historico: a.txHistoricoMedico ?? '' }),
                errorMsg: 'Falha ao carregar anotações do portal.'
            },
            rgm: {
                service: (p, page, size, direction) => this.anotacoesService.buscarRgm(p, page, size, direction),
                mapper: (a, idx) => ({ codigo: (idx ?? 0) + 1, data: this.formatarData(a.dtOcorrencia), historico: a.txObservacao ?? '' }),
                errorMsg: 'Falha ao carregar anotações do RGM.'
            },
            provisorio: {
                service: (p, page, size, direction) => this.anotacoesService.buscarVistoProvisorio(p, page, size, direction),
                mapper: (a) => ({ codigo: a.idHistoricoMedico ?? 0, data: this.formatarData(a.dtHistoricoMedico), historico: a.txHistoricoMedico ?? '' }),
                errorMsg: 'Falha ao carregar anotações de visto provisório.'
            },
            documentos: {
                service: (p, page, size, direction) => this.anotacoesService.buscarDocumentos(p, page, size, direction),
                mapper: (a) => ({ codigo: a.idHistoricoMedico ?? 0, data: this.formatarData(a.dtHistoricoMedico), historico: a.txHistoricoMedico ?? '' }),
                errorMsg: 'Falha ao carregar anotações de documentos.'
            },
            eleicoes: {
                service: (p, page, size, direction) => this.anotacoesService.buscarEleicoes(p, page, size, direction),
                mapper: (a) => ({ codigo: a.idHistoricoMedico ?? 0, data: this.formatarData(a.dtHistoricoMedico), historico: a.txHistoricoMedico ?? '' }),
                errorMsg: 'Falha ao carregar anotações de eleições.'
            }
        };

        return configs[key];
    }

    abrirDialogNovaAnotacao(): void {
        if (!this.podeGerenciarAnotacoesGerais()) return;
        this.chaveIdempotencia = novaChaveIdempotencia();
        this.modalNovaAnotacao = true;
    }

    visualizarAnotacao(anotacao: AnotacaoRow): void {
        this.anotacaoVisualizacao.set(anotacao);
        this.modalVisualizacaoAnotacao = true;
    }

    fecharVisualizacaoAnotacao(): void {
        this.modalVisualizacaoAnotacao = false;
        this.anotacaoVisualizacao.set(null);
    }

    onAnotacaoCriada(payload: { dtHistoricoMedico: string; txHistoricoMedico: string }): void {
        if (!this.podeGerenciarAnotacoesGerais()) return;

        const p = this.pessoa();
        if (!p) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Pessoa não informada.' });
            return;
        }

        // Reaproveita a mesma conversão usada na exibição da lista (yyyy-MM-dd/Date → dd/MM/yyyy, formato aceito pelo backend).
        const dataHistorico = this.formatarData(payload.dtHistoricoMedico) || undefined;
        const body: MedicoAnotacaoInsertRequest = { texto: payload.txHistoricoMedico, dataHistorico };
        this.loading.set(true);
        this.anotacoesService
            .inserirAnotacao({ id: p.id, uf: p.uf }, body, comIdempotencia(this.chaveIdempotencia))
            .pipe(finalize(() => this.loading.set(false)))
            .subscribe({
                next: (ok) => {
                    if (ok) {
                        this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: 'Anotação criada com sucesso.' });
                        this.resetPaginacao();
                        this.atualizarTabela();
                        this.modalNovaAnotacao = false;
                    } else {
                        this.messageService.add({ severity: 'warn', summary: 'Aviso', detail: 'Não foi possível criar a anotação.' });
                    }
                },
                error: (err) => {
                    console.error('Falha ao inserir anotação', err);
                    this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Falha ao inserir anotação.' });
                }
            });
    }

    private formatarData(valor?: string | null): string {
        if (!valor) return '';
        const m = String(valor).match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (m) {
            const [, yyyy, mm, dd] = m as unknown as [string, string, string, string];
            return `${dd}/${mm}/${yyyy}`;
        }
        try {
            const d = new Date(valor);
            const dd = String(d.getDate()).padStart(2, '0');
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const yyyy = d.getFullYear();
            return `${dd}/${mm}/${yyyy}`;
        } catch {
            return String(valor);
        }
    }

    get selectedLabel(): string {
        return this.options.find((o) => o.value === (this.selectedValue ?? 'gerais'))?.label ?? 'Tipo de Anotação';
    }

    remover(idHistoricoMedico: number) {
        if (!this.podeGerenciarAnotacoesGerais()) {
            return;
        }

        const p = this.pessoa();
        if (!p || p.id == null || !p.uf) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Pessoa não informada.' });
            return;
        }

        this.confirmationService.confirm({
            message: 'Tem certeza que deseja remover esta anotação?',
            header: 'Confirmar Exclusão',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Sim',
            rejectLabel: 'Não',
            accept: () => {
                this.loading.set(true);
                this.anotacoesService
                    .excluirAnotacao({ id: p.id, uf: p.uf }, idHistoricoMedico)
                    .pipe(finalize(() => this.loading.set(false)))
                    .subscribe({
                        next: (ok) => {
                            if (ok) {
                                this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: 'Anotação removida com sucesso.' });
                            } else {
                                this.messageService.add({ severity: 'warn', summary: 'Aviso', detail: 'Anotação não encontrada para exclusão.' });
                            }
                            this.atualizarTabela();
                        },
                        error: (err) => {
                            console.error('Falha ao excluir anotação', err);
                            this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Falha ao excluir anotação.' });
                        }
                    });
            },
            reject: () => {
                this.messageService.add({
                    severity: 'info',
                    summary: 'Ação Cancelada',
                    detail: 'Nenhuma alteração foi salva.'
                });
            }
        });
    }

    public getStatusSeverity(): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' {
        return 'info';
    }
}
